import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface FeatureFlags {
  // Core features
  feature_ai_chat: boolean;
  feature_name_swipe: boolean;
  feature_name_suggestions: boolean;
  feature_appointments: boolean;
  feature_contraction_counter: boolean;
  feature_checklists: boolean;
  feature_product_check: boolean;
  feature_barcode_scanner: boolean;
  feature_authority_guides: boolean;
  feature_pregnancy_card: boolean;
  feature_partner_sync: boolean;
  feature_data_export: boolean;
  // Baby Care feature
  baby_care_enabled: boolean;
  // Baby Growth feature
  baby_growth_enabled: boolean;
  // Sleep Plan feature
  sleep_plan_enabled: boolean;
  // Payment features
  feature_native_iap: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  feature_ai_chat: true,
  feature_name_swipe: true,
  feature_name_suggestions: true,
  feature_appointments: true,
  feature_contraction_counter: true,
  feature_checklists: true,
  feature_product_check: true,
  feature_barcode_scanner: true,
  feature_authority_guides: true,
  feature_pregnancy_card: true,
  feature_partner_sync: true,
  feature_data_export: true,
  // Baby Care - defaults to true
  baby_care_enabled: true,
  // Baby Growth - defaults to true
  baby_growth_enabled: true,
  // Sleep Plan - defaults to true
  sleep_plan_enabled: true,
  // Payment - defaults to true (IAP enabled on native)
  feature_native_iap: true,
};

let cachedFlags: FeatureFlags | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlags>(cachedFlags || DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState(!cachedFlags);

  const loadFlags = useCallback(async (forceRefresh = false) => {
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && cachedFlags && Date.now() - cacheTimestamp < CACHE_TTL) {
      setFlags(cachedFlags);
      setIsLoading(false);
      return;
    }

    try {
      // Load feature flags and baby feature settings
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .or('key.like.feature_%,key.like.baby_%_enabled,key.like.sleep_%_enabled');

      if (error) {
        console.error('Error loading feature flags:', error);
        return;
      }

      const loadedFlags: FeatureFlags = { ...DEFAULT_FLAGS };

      (data as { key: string; value: unknown }[])?.forEach((setting) => {
        const key = setting.key as keyof FeatureFlags;
        if (key in loadedFlags) {
          // Handle boolean values from JSONB
          loadedFlags[key] = setting.value === true || setting.value === 'true';
        }
      });

      cachedFlags = loadedFlags;
      cacheTimestamp = Date.now();
      setFlags(loadedFlags);
    } catch (err) {
      console.error('Error loading feature flags:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const isEnabled = useCallback(
    (feature: keyof FeatureFlags): boolean => {
      return flags[feature] ?? true;
    },
    [flags]
  );

  const refresh = useCallback(() => {
    loadFlags(true);
  }, [loadFlags]);

  return {
    flags,
    isLoading,
    isEnabled,
    refresh,
  };
}

// Convenience hook for checking a single feature
export function useFeatureEnabled(feature: keyof FeatureFlags): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(feature);
}
